import React, { useEffect, useState, useCallback } from "react";
import {
  FileSpreadsheet, Plus, Download, Copy, Trash2, Pencil, CheckCircle2, ArrowRightLeft,
  Files, Users, Settings as SettingsIcon, DollarSign, Crown, Check, Upload, RefreshCw,
  Mail, Landmark, History, X,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import DocumentEditor from "@/components/app/DocumentEditor";
import { toast } from "sonner";

const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", CAD: "$", AUD: "$", INR: "₹" };
const STATUS_STYLES = {
  paid: "bg-green-500/10 text-green-400 border-green-500/20",
  partial: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  sent: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  draft: "bg-white/5 text-zinc-400 border-white/10",
  overdue: "bg-red-500/10 text-red-400 border-red-500/20",
  accepted: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

function Paywall({ onSubscribe, busy }) {
  return (
    <div className="max-w-3xl mx-auto text-center py-10" data-testid="generator-paywall">
      <div className="w-16 h-16 rounded-2xl bg-green-500/10 text-green-400 border border-green-500/20 flex items-center justify-center mx-auto mb-6"><FileSpreadsheet size={30} /></div>
      <h1 className="text-3xl font-bold font-display">Invoice &amp; Proposal Generator</h1>
      <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Create branded invoices, quotes &amp; proposals, save your clients, take payments online, and track what's paid — all in one place.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10 text-left">
        <div className="glass-panel rounded-2xl p-6 border border-white/10">
          <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Monthly</div>
          <div className="text-4xl font-bold text-white mt-2">$10<span className="text-base text-zinc-500 font-medium">/mo</span></div>
          <button onClick={() => onSubscribe("monthly")} disabled={busy} className="w-full mt-5 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition disabled:opacity-60" data-testid="subscribe-monthly-btn">Subscribe Monthly</button>
        </div>
        <div className="glass-panel rounded-2xl p-6 border border-green-500/30 relative shadow-[0_0_30px_rgba(34,197,94,0.12)]">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-green-500 text-black text-[10px] font-bold uppercase tracking-wider">Best Value</div>
          <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Yearly</div>
          <div className="text-4xl font-bold text-white mt-2">$110<span className="text-base text-zinc-500 font-medium">/yr</span></div>
          <div className="text-xs text-green-400 mt-1">Save ~$10 vs monthly</div>
          <button onClick={() => onSubscribe("yearly")} disabled={busy} className="w-full mt-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition disabled:opacity-60" data-testid="subscribe-yearly-btn">Subscribe Yearly</button>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 text-left max-w-xl mx-auto text-sm text-zinc-300">
        {["Unlimited invoices, quotes & proposals", "Branded PDF export with your logo", "Online 'Pay Now' checkout", "Client list, tax, discounts & tracking"].map((f) => (
          <div key={f} className="flex items-center gap-2"><Check size={15} className="text-green-400 flex-shrink-0" /> {f}</div>
        ))}
      </div>
    </div>
  );
}

function PaymentHistory({ doc, money, onClose }) {
  const payments = doc.payments || [];
  const fmt = (s) => { try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="payment-history-modal">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-lg border border-white/10 fade-in overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <h3 className="text-base font-bold text-white flex items-center gap-2"><History size={16} className="text-primary-400" /> Payment History — {doc.number}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="payment-history-close"><X /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-xs text-zinc-500">Total</div><div className="text-lg font-bold text-white">{money(doc.total)}</div></div>
            <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20"><div className="text-xs text-zinc-500">Paid</div><div className="text-lg font-bold text-green-400">{money(doc.amount_paid)}</div></div>
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20"><div className="text-xs text-zinc-500">Balance</div><div className="text-lg font-bold text-amber-400">{money(doc.balance)}</div></div>
          </div>
          <div className="space-y-2">
            {payments.length === 0 && <p className="text-center text-zinc-500 text-sm py-6">No payments recorded yet.</p>}
            {payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/10" data-testid="payment-history-row">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 text-green-400 flex items-center justify-center"><CheckCircle2 size={15} /></div>
                  <div>
                    <div className="text-sm font-bold text-white">{money(p.amount)}</div>
                    <div className="text-[11px] text-zinc-500">{fmt(p.at)} · {p.method === "manual" ? "Marked paid" : "Stripe"}</div>
                  </div>
                </div>
                {doc.deposit_pct > 0 && i === 0 && <span className="text-[10px] px-2 py-0.5 rounded bg-primary-500/10 text-primary-300 border border-primary-500/20 font-bold">Deposit</span>}
              </div>
            ))}
          </div>
          {doc.deposit_pct > 0 && <p className="text-[11px] text-zinc-500">Required deposit: {doc.deposit_pct}% ({money(doc.deposit_amount)}) · {doc.deposit_met ? "met ✓" : "not yet met"}</p>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-panel p-5 rounded-2xl flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${color}`}><Icon size={20} /></div>
      <div><div className="text-2xl font-bold text-white">{value}</div><div className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">{label}</div></div>
    </div>
  );
}

export default function Generator() {
  const { profile } = useApp();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editor, setEditor] = useState(null); // {doc, type}
  const [history, setHistory] = useState(null); // doc for payment history
  const [clientForm, setClientForm] = useState({ name: "", company: "", email: "", phone: "", address: "" });
  const [logoBust, setLogoBust] = useState(Date.now());
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const symbol = CURRENCY_SYMBOLS[settings?.currency] || "$";
  const money = (v) => `${symbol}${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const loadAll = useCallback(async () => {
    const [st, inv, qt, cl, se] = await Promise.all([
      api.genDashboard(), api.listGenDocuments("invoice"), api.listGenDocuments("quote"), api.listGenClients(), api.getGenSettings(),
    ]);
    setStats(st); setInvoices(inv); setQuotes(qt); setClients(cl); setSettings(se);
  }, []);

  useEffect(() => {
    api.getSubscriptionStatus().then((s) => {
      setSub(s);
      if (s.active) {
        loadAll().catch(() => {});
        api.genConnectStatus().then(setConnect).catch(() => setConnect({ connected: false, ready: false }));
      }
    }).catch(() => setSub({ active: false }));
  }, [loadAll]);

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const res = await api.subscriptionCheckout(plan, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const refresh = () => loadAll().catch(() => {});

  const copyLink = (pubId) => {
    navigator.clipboard?.writeText(`${window.location.origin}/doc/${pubId}`).then(
      () => toast.success("Shareable link copied"), () => toast.error("Copy failed"));
  };

  const markPaid = async (id) => { await api.markGenPaid(id); toast.success("Marked as paid"); refresh(); };
  const emailDoc = async (r) => {
    if (!r.client?.email) { toast.error("Add an email to this client first"); return; }
    try { await api.sendGenDocument(r.id); toast.success(`Emailed to ${r.client.email}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };
  const startConnect = async () => {
    setConnecting(true);
    try { const res = await api.genConnectStart(window.location.origin); window.location.href = res.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start onboarding"); setConnecting(false); }
  };
  const refreshConnect = async () => { try { setConnect(await api.genConnectStatus()); } catch { /* ignore */ } };
  const convert = async (id) => { const inv = await api.convertGenQuote(id); toast.success(`Converted to invoice ${inv.number}`); refresh(); setTab("invoices"); };
  const duplicate = async (id) => { await api.duplicateGenDocument(id); toast.success("Duplicated as draft"); refresh(); };
  const removeDoc = async (id) => { await api.deleteGenDocument(id); toast.success("Deleted"); refresh(); };
  const removeClient = async (id) => { await api.deleteGenClient(id); toast.success("Client removed"); refresh(); };

  const saveClient = async () => {
    if (!clientForm.name.trim()) { toast.error("Name required"); return; }
    await api.createGenClient(clientForm);
    setClientForm({ name: "", company: "", email: "", phone: "", address: "" });
    toast.success("Client added");
    refresh();
  };

  const saveSettings = async () => {
    const s = await api.updateGenSettings(settings);
    setSettings(s);
    toast.success("Settings saved");
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await api.uploadGenLogo(file);
      const s = await api.getGenSettings();
      setSettings(s);
      setLogoBust(Date.now());
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  if (sub === null) return <div className="h-full flex items-center justify-center text-zinc-500">Loading…</div>;

  if (!sub.active) {
    return (
      <div className="h-full overflow-y-auto px-6 md:px-10 py-10 fade-in">
        <Paywall onSubscribe={subscribe} busy={busy} />
      </div>
    );
  }

  const DocTable = ({ rows, kind }) => (
    <div className="glass-panel rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10">
            <tr><th className="px-5 py-3.5">#</th><th className="px-5 py-3.5">Client</th><th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">Total</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5 bg-black/20">
            {rows.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-500">No {kind}s yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-white/5 transition-colors" data-testid={`${kind}-row`}>
                <td className="px-5 py-3.5 font-bold text-white">{r.number}</td>
                <td className="px-5 py-3.5 text-zinc-300">{r.client?.name || "—"}</td>
                <td className="px-5 py-3.5 text-zinc-500">{r.issue_date || "—"}</td>
                <td className="px-5 py-3.5 text-white font-bold">
                  {money(r.total)}
                  {kind === "invoice" && r.payment_state === "partial" && <div className="text-[10px] text-amber-400 font-medium">{money(r.balance)} due</div>}
                </td>
                <td className="px-5 py-3.5">{(() => { const badge = kind === "invoice" && r.payment_state === "partial" ? "partial" : r.payment_state === "paid" ? "paid" : r.overdue ? "overdue" : r.status; return <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${STATUS_STYLES[badge] || STATUS_STYLES.draft}`}>{badge}</span>; })()}</td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Edit" onClick={() => setEditor({ doc: r, type: kind })} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"><Pencil size={13} /></button>
                    <a title="PDF" href={api.genDocPdfUrl(r.public_id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white" data-testid="doc-pdf-btn"><Download size={13} /></a>
                    <button title="Copy link" onClick={() => copyLink(r.public_id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white" data-testid="doc-copylink-btn"><Copy size={13} /></button>
                    <button title="Email to client" onClick={() => emailDoc(r)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white" data-testid="doc-email-btn"><Mail size={13} /></button>
                    {kind === "quote" && <button title="Convert to invoice" onClick={() => convert(r.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"><ArrowRightLeft size={13} /></button>}
                    {kind === "invoice" && <button title="Payment history" onClick={() => setHistory(r)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white" data-testid="doc-history-btn"><History size={13} /></button>}
                    {kind === "invoice" && r.payment_state !== "paid" && <button title="Mark paid" onClick={() => markPaid(r.id)} className="p-2 rounded-lg bg-white/5 text-green-400 hover:bg-green-500/20"><CheckCircle2 size={13} /></button>}
                    <button title="Duplicate" onClick={() => duplicate(r.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"><Files size={13} /></button>
                    <button title="Delete" onClick={() => removeDoc(r.id)} className="p-2 rounded-lg bg-white/5 text-zinc-300 hover:bg-red-500/20 hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const inputCls = "w-full px-4 py-2.5 rounded-lg dark-input text-sm";
  const labelCls = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="h-full overflow-y-auto px-6 md:px-10 py-8 fade-in" data-testid="generator-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-display flex items-center gap-2"><FileSpreadsheet size={26} className="text-green-400" /> Invoice &amp; Proposal Generator</h1>
          <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2">{sub.admin ? <><Crown size={13} className="text-yellow-400" /> Admin access</> : <><span className="text-green-400 font-bold">● Active</span> subscription</>}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} className="px-3 py-2.5 rounded-xl bg-white/5 text-zinc-300 hover:bg-white/10" title="Refresh"><RefreshCw size={16} /></button>
          <button onClick={() => setEditor({ doc: null, type: tab === "quotes" ? "quote" : "invoice" })} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 flex items-center" data-testid="new-document-btn"><Plus size={15} className="mr-1.5" /> New {tab === "quotes" ? "Quote" : "Invoice"}</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto hide-scrollbar">
        {[["overview", "Overview"], ["invoices", "Invoices"], ["quotes", "Quotes"], ["clients", "Clients"], ["settings", "Settings"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-primary-500 text-white" : "border-transparent text-zinc-500 hover:text-white"}`} data-testid={`gen-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="Paid" value={money(stats?.paid_total)} color="bg-green-500/10 text-green-400 border-green-500/20" />
            <StatCard icon={DollarSign} label="Outstanding" value={money(stats?.outstanding_total)} color="bg-orange-500/10 text-orange-400 border-orange-500/20" />
            <StatCard icon={Files} label="Invoices" value={stats?.invoice_count ?? 0} color="bg-blue-500/10 text-blue-400 border-blue-500/20" />
            <StatCard icon={Users} label="Clients" value={stats?.client_count ?? 0} color="bg-purple-500/10 text-purple-400 border-purple-500/20" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Recent Invoices</h3>
            <DocTable rows={invoices.slice(0, 5)} kind="invoice" />
          </div>
        </div>
      )}

      {tab === "invoices" && <DocTable rows={invoices} kind="invoice" />}
      {tab === "quotes" && <DocTable rows={quotes} kind="quote" />}

      {tab === "clients" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-zinc-400 uppercase text-xs font-bold tracking-wider border-b border-white/10"><tr><th className="px-5 py-3.5">Name</th><th className="px-5 py-3.5">Company</th><th className="px-5 py-3.5">Email</th><th className="px-5 py-3.5 text-right"></th></tr></thead>
                <tbody className="divide-y divide-white/5 bg-black/20">
                  {clients.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-zinc-500">No clients yet.</td></tr>}
                  {clients.map((c) => (
                    <tr key={c.id} className="hover:bg-white/5" data-testid="client-row">
                      <td className="px-5 py-3.5 font-bold text-white">{c.name}</td>
                      <td className="px-5 py-3.5 text-zinc-300">{c.company || "—"}</td>
                      <td className="px-5 py-3.5 text-zinc-500">{c.email || "—"}</td>
                      <td className="px-5 py-3.5 text-right"><button onClick={() => removeClient(c.id)} className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:bg-red-500/20 hover:text-red-400"><Trash2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="glass-panel rounded-2xl p-6 space-y-3 self-start">
            <h3 className="font-bold text-white text-sm">Add Client</h3>
            <input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="Name *" className={inputCls} data-testid="client-name-input" />
            <input value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} placeholder="Company" className={inputCls} />
            <input value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="Email" className={inputCls} />
            <input value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} placeholder="Phone" className={inputCls} />
            <input value={clientForm.address} onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })} placeholder="Address" className={inputCls} />
            <button onClick={saveClient} className="w-full py-2.5 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500" data-testid="save-client-btn">Add Client</button>
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="max-w-2xl space-y-5">
          <div className="glass-panel rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-white text-sm flex items-center gap-2"><SettingsIcon size={16} className="text-primary-400" /> Business & Branding</h3>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                {settings.logo_path ? <img src={`${api.genLogoUrl(profile.id)}?t=${logoBust}`} alt="logo" className="w-full h-full object-contain" /> : <FileSpreadsheet size={24} className="text-zinc-600" />}
              </div>
              <label className="px-4 py-2.5 rounded-lg bg-white/10 text-white text-sm font-bold hover:bg-white/20 cursor-pointer flex items-center" data-testid="logo-upload-label">
                <Upload size={14} className="mr-2" /> Upload Logo
                <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-upload-input" />
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className={labelCls}>Business Name</label><input value={settings.business_name || ""} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} className={inputCls} data-testid="settings-business-name" /></div>
              <div><label className={labelCls}>Business Email</label><input value={settings.business_email || ""} onChange={(e) => setSettings({ ...settings, business_email: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Phone</label><input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Address</label><input value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className={inputCls} /></div>
            </div>
          </div>
          <div className="glass-panel rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-white text-sm">Defaults</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className={labelCls}>Invoice Prefix</label><input value={settings.invoice_prefix || ""} onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Quote Prefix</label><input value={settings.quote_prefix || ""} onChange={(e) => setSettings({ ...settings, quote_prefix: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Currency</label>
                <select value={settings.currency || "USD"} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className={inputCls}>
                  {Object.keys(CURRENCY_SYMBOLS).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Default Tax %</label><input type="number" value={settings.tax_default || 0} onChange={(e) => setSettings({ ...settings, tax_default: Number(e.target.value) })} className={inputCls} /></div>
              <div><label className={labelCls}>Default Deposit %</label><input type="number" min="0" max="100" value={settings.default_deposit_pct || 0} onChange={(e) => setSettings({ ...settings, default_deposit_pct: Number(e.target.value) })} className={inputCls} data-testid="settings-default-deposit" /></div>
            </div>
            <button onClick={saveSettings} className="px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500" data-testid="save-settings-btn">Save Settings</button>
          </div>

          <div className="glass-panel rounded-2xl p-6 space-y-4" data-testid="connect-card">
            <h3 className="font-bold text-white text-sm flex items-center gap-2"><Landmark size={16} className="text-green-400" /> Direct Payouts (get paid to your bank)</h3>
            <p className="text-xs text-zinc-400">Connect your bank via Stripe so invoice payments land directly in your account. Until connected, payments are held on the platform balance.</p>
            {connect?.ready ? (
              <div className="flex items-center gap-2 text-sm text-green-400 font-bold"><CheckCircle2 size={16} /> Connected — payouts enabled</div>
            ) : connect?.connected ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-orange-400 font-bold">Onboarding incomplete</span>
                <button onClick={startConnect} disabled={connecting} className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-bold hover:bg-white/20 disabled:opacity-60" data-testid="connect-continue-btn">Continue Setup</button>
                <button onClick={refreshConnect} className="px-3 py-2 bg-white/5 text-zinc-300 rounded-lg text-sm hover:bg-white/10"><RefreshCw size={14} /></button>
              </div>
            ) : (
              <button onClick={startConnect} disabled={connecting} className="px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-500 flex items-center disabled:opacity-60" data-testid="connect-bank-btn">
                <Landmark size={15} className="mr-2" /> {connecting ? "Redirecting…" : "Connect Bank Account"}
              </button>
            )}
          </div>
        </div>
      )}

      {editor && (
        <DocumentEditor
          doc={editor.doc}
          type={editor.type}
          clients={clients}
          settings={settings}
          symbol={symbol}
          onClose={() => setEditor(null)}
          onSaved={refresh}
          onClientAdded={(c) => setClients((cs) => [c, ...cs])}
        />
      )}
      {history && <PaymentHistory doc={history} money={money} onClose={() => setHistory(null)} />}
    </div>
  );
}
